import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, VStack, useToast } from "@chakra-ui/react";
import CustomLoader from "../../components/placeholders/CustomLoader";
import AudioCard from "../../components/cards/AudioCard";
import {
  CARD_SPACING,
  DEFAULT_WIDTH,
  PAGE_PADDING,
} from "../../helpers/constant";
import SearchBar from "../../components/UI/SearchBar";
import { useRecordings } from "../../context/RecordingContext";
import { extractApiErrorMessage, formatDate } from "../../helpers/utils";
import EmptyState from "../../components/placeholders/EmptyState";
import { MoonStarsIcon } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { useSharedRecordingQuery } from "../../hooks/useSharedRecordingQuery";
import { Recording } from "../../model/recording";

const PAGE_SIZE = 10;

const PrayerVigilPage: React.FC = () => {
  const toast = useToast();
  const { t } = useTranslation();
  const {
    hasInitialized,
    setHasInitialized,
    recordingsByCategory,
    hasLoadedCategory,
    hasLoadedFavoriteIDs,
    listFavoriteIDs,
    listRecordingsByCategory,
    addFavorite,
    removeFavorite,
    isFavorite,
    getRecordingFiles,
  } = useRecordings();

  const {
    searchMode,
    searchQuery,
    setSearchQuery,
    sharedRecording,
    isLoadingSharedRecording,
    selectSharedRecording,
  } = useSharedRecordingQuery({
    matchRecording: (recording: Recording) =>
      recording.category === "prayer_vigil",
    onLoadError: (error: unknown) => {
      toast({
        title: t("pages.nightVigils.loadErrorTitle"),
        description: extractApiErrorMessage(
          error,
          String(t("common.tryAgain")),
        ),
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
    },
  });

  const [nextPageToken, setNextPageToken] = useState<string | undefined>();
  const [isFetching, setIsFetching] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const recordings = useMemo(
    () => recordingsByCategory.prayer_vigil ?? [],
    [recordingsByCategory.prayer_vigil],
  );

  const loadRecordingsPage = useCallback(
    async (reset: boolean) => {
      if (reset) {
        setIsFetching(true);
      } else {
        setIsLoadingMore(true);
      }

      try {
        const result = await listRecordingsByCategory("prayer_vigil", {
          limit: PAGE_SIZE,
          nextPageToken: reset ? undefined : nextPageToken,
        });
        setNextPageToken(result.nextPageToken);
      } finally {
        setIsFetching(false);
        setIsLoadingMore(false);
      }
    },
    [listRecordingsByCategory, nextPageToken],
  );

  useEffect(() => {
    if (hasInitialized.prayer_vigil) {
      return;
    }

    let isMounted = true;

    const initialize = async () => {
      try {
        if (!hasLoadedFavoriteIDs) {
          await listFavoriteIDs();
        }

        if (!hasLoadedCategory("prayer_vigil")) {
          await loadRecordingsPage(true);
        }
      } catch (error) {
        toast({
          title: t("pages.nightVigils.loadErrorTitle"),
          description: extractApiErrorMessage(
            error,
            String(t("common.tryAgain")),
          ),
          status: "error",
          duration: 4000,
          isClosable: true,
          position: "top",
        });
      } finally {
        if (isMounted) {
          setHasInitialized((prev) => ({ ...prev, prayer_vigil: true }));
        }
      }
    };

    initialize();

    return () => {
      isMounted = false;
    };
  }, [
    hasInitialized.prayer_vigil,
    hasLoadedCategory,
    hasLoadedFavoriteIDs,
    listFavoriteIDs,
    loadRecordingsPage,
    setHasInitialized,
    t,
    toast,
  ]);

  const displayedRecordings = useMemo(() => {
    if (sharedRecording) {
      return [sharedRecording];
    }

    const baseRecordings = recordings;
    const normalizedQuery = searchQuery.trim().toLowerCase();

    if (!normalizedQuery) {
      return baseRecordings;
    }

    return baseRecordings.filter((recording) => {
      const searchableText = [
        recording.title,
        recording.preacherName,
        recording.rubricTitle,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(normalizedQuery);
    });
  }, [recordings, searchQuery, sharedRecording]);

  const hasNoData = !sharedRecording && recordings.length === 0;
  const hasNoResults =
    searchQuery.trim() !== "" && displayedRecordings.length === 0 && !hasNoData;

  const handleLoadMore = async () => {
    if (!nextPageToken || isLoadingMore) {
      return;
    }

    try {
      await loadRecordingsPage(false);
    } catch (error) {
      toast({
        title: t("pages.nightVigils.loadErrorTitle"),
        description: extractApiErrorMessage(
          error,
          String(t("common.tryAgain")),
        ),
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
    }
  };

  return (
    <VStack w={DEFAULT_WIDTH} px={PAGE_PADDING} mx="auto" align="start">
      <SearchBar
        value={searchQuery}
        onSearch={setSearchQuery}
        placeholder={String(t("pages.nightVigils.searchPlaceholder"))}
        mb={7}
        mode={searchMode}
        onSuggestionSelect={(item) => selectSharedRecording(item.recordingID)}
      />
      {isFetching ||
      isLoadingSharedRecording ||
      !hasInitialized.prayer_vigil ? (
        <CustomLoader />
      ) : hasNoData ? (
        <EmptyState
          icon={MoonStarsIcon}
          title={t("pages.nightVigils.emptyTitle")}
          description={t("pages.nightVigils.emptyDescription")}
        />
      ) : hasNoResults ? (
        <EmptyState
          icon={MoonStarsIcon}
          title={t("pages.search.noResultsTitle")}
          description={t("pages.search.noResultsDescription")}
        />
      ) : (
        <VStack spacing={CARD_SPACING} w="full">
          {displayedRecordings.map((recording) => (
            <AudioCard
              key={recording.recordingID}
              recordingID={recording.recordingID}
              title={recording.title}
              preacherName={recording.preacherName}
              date={formatDate(recording.date)}
              hasRecording={Boolean(recording.recordingS3Key)}
              hasSummary={Boolean(recording.summaryS3Key)}
              youtubeLink={recording.youtubeLink}
              saved={isFavorite(recording.recordingID)}
              onAddFavorite={() =>
                addFavorite(recording.recordingID, recording)
              }
              onRemoveFavorite={removeFavorite}
              onRequestFiles={(recordingID) => getRecordingFiles(recordingID)}
            />
          ))}

          {!sharedRecording && nextPageToken && (
            <Button
              mt={2}
              alignSelf="center"
              onClick={handleLoadMore}
              isLoading={isLoadingMore}
            >
              {t("common.loadMore")}
            </Button>
          )}
        </VStack>
      )}
    </VStack>
  );
};

export default PrayerVigilPage;
